import { PageToolbar } from '@app/components/page-toolbar/PageToolbar';
import { ChipNavigationMui } from '@mi-front/ui';
import { Box } from '@mui/material';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Redirect, useLocation, useRouteMatch } from 'react-router-dom';
import { useAdoverlayEnabledForActiveStation } from '../hooks/useAdoverlayEnabled';
import { useMentionsEnabledForActiveStation } from '../hooks/useMentionsEnabledForActiveStation';
import { MatchCategory } from '../screens/matches-list/types';

type MatchCategoryMeta = {
  id: string;
  path: string;
  label: string;
};

export const AuditMatchCategoryNavigator = ({
  children,
  additionalCategories = [],
}: {
  children: (category: string) => React.ReactNode;
  additionalCategories?: MatchCategoryMeta[];
}) => {
  const match = useRouteMatch();
  const location = useLocation();
  const mentionsEnabled = useMentionsEnabledForActiveStation();
  const adOverlayEnabled = useAdoverlayEnabledForActiveStation();

  const pathBase = match.url.replace(/\/$/, '') || match.url;
  const isAtBasePath = location.pathname === pathBase || location.pathname === pathBase + '/';

  const chips = React.useMemo(() => {
    const items = [
      {
        value: 'spot',
        // Short 4-letter label; minWidth keeps the chip from looking cramped next to MENTIONS.
        label: (
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              minWidth: (theme) => theme.spacing(8),
              textAlign: 'center',
            }}
          >
            <FormattedMessage id="audit.matchCategory.customAds" defaultMessage="SPOT" />
          </Box>
        ),
        content: children(MatchCategory.ADS),
      },
      ...(mentionsEnabled
        ? [
            {
              value: 'mentions',
              label: (
                <FormattedMessage id="audit.matchCategory.mentions" defaultMessage="MENTIONS" />
              ),
              content: children(MatchCategory.LIVE_MENTIONS),
            },
          ]
        : []),
      ...(adOverlayEnabled
        ? [
            {
              value: 'adoverlay',
              label: (
                <FormattedMessage
                  id="audit.matchCategory.adOverlays"
                  defaultMessage="AD OVERLAYS"
                />
              ),
              content: children(MatchCategory.AD_OVERLAY),
            },
          ]
        : []),
      ...(mentionsEnabled
        ? additionalCategories.map(({ id, path, label }) => ({
            value: path,
            label,
            content: children(id),
          }))
        : []),
    ];
    return items;
  }, [children, mentionsEnabled, adOverlayEnabled, additionalCategories]);

  if (isAtBasePath) {
    return <Redirect to={`${pathBase}/spot`} />;
  }

  return (
    <ChipNavigationMui
      pathBase={pathBase}
      defaultActiveChip="spot"
      chips={chips}
      Wrapper={PageToolbar}
    />
  );
};
